// Pieces shared by the Payment Link Generator's personal tab, shareable tab and the
// link detail dialog.
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import { lightDark, DARK } from "utils/lightDark";
import { fmtMoney } from "utils/flexiblePayment";

// This page's accent colours and their dark-mode partners (the light ones vanish on navy).
export const INDIGO_TEXT = lightDark("#1a237e", "#9fa8da");
export const STRIPE_TEXT = lightDark("#635bff", "#a5a0ff");
export const PAYSTACK_TEXT = lightDark("#00996a", "#4cd6a4");
export const PANEL_LINE = lightDark("#e0e0e0", DARK.border);
export const SCHEDULE_BORDER = lightDark("1px solid #c5cae9", `1px solid ${DARK.border}`);
export const ROW_LINE = lightDark("1px solid #f0f0f0", `1px solid ${DARK.border}`);

export const CURRENCIES = ["NGN", "USD", "GBP"];

export const COUNTRIES = [
  "Nigeria",
  "United Kingdom",
  "United States",
  "Ghana",
  "Kenya",
  "South Africa",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Other",
];

export function gatewayForCurrency(currency) {
  return currency === "NGN" ? "paystack" : "stripe";
}

export const fmt = fmtMoney;

export const STATUS_COLOR = {
  active: "info",
  paid: "success",
  partially_paid: "warning",
  expired: "default",
  cancelled: "error",
};

// Mirrors the server's CADENCE table (utils/paymentLinkMessages.js) — 'biweekly' is
// Stripe-only (Paystack has no native "every 2 weeks" plan interval); the rest map
// 1:1 onto both gateways.
export const CADENCE = {
  daily: { unit: "day", amount: 1, label: "day", option: "Daily" },
  weekly: { unit: "day", amount: 7, label: "week", option: "Weekly" },
  biweekly: { unit: "day", amount: 14, label: "2 weeks", option: "Biweekly", stripeOnly: true },
  monthly: { unit: "month", amount: 1, label: "month", option: "Monthly" },
  quarterly: { unit: "month", amount: 3, label: "3 months", option: "Quarterly (every 3 months)" },
  biannually: {
    unit: "month",
    amount: 6,
    label: "6 months",
    option: "Biannually (every 6 months)",
  },
  annually: { unit: "month", amount: 12, label: "year", option: "Annually" },
};

export function defaultStartDateFor(cadence) {
  const cfg = CADENCE[cadence] || CADENCE.monthly;
  const d = new Date();
  if (cfg.unit === "day") d.setDate(d.getDate() + cfg.amount);
  else d.setMonth(d.getMonth() + cfg.amount);
  return d.toISOString().split("T")[0];
}

export function cadenceWord(cadence) {
  return (CADENCE[cadence] || CADENCE.monthly).label;
}

export function StatusChip({ status }) {
  return (
    <Chip
      label={status?.replace("_", " ") || "—"}
      color={STATUS_COLOR[status] || "default"}
      size="small"
      sx={{ textTransform: "capitalize", fontWeight: 600 }}
    />
  );
}

export function NotifIcon({ sent, label }) {
  return (
    <Tooltip title={`${label}: ${sent ? "Sent" : "Not sent"}`}>
      <Icon sx={{ color: sent ? "#4caf50" : "#bdbdbd", fontSize: "1.1rem" }}>
        {sent ? "check_circle" : "cancel"}
      </Icon>
    </Tooltip>
  );
}

// Below md the table becomes stacked cards: ~40px tap targets for icon buttons, and the
// regular 40px height for small buttons (theme small = 2rem).
export const ICON_TAP_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };
export const SMALL_BUTTON_TAP_SX = { minHeight: { xs: "2.5rem", md: "2rem" } };

// The theme's Select override strips its vertical padding, so Selects render ~20px tall.
// Where the form fields stack full-width (below md), match the 37px small TextField height
// (doubled class so it outranks the Select's own later-inserted min-height rule).
export const selectTapSx = (theme) => ({
  [theme.breakpoints.down("md")]: { "& .MuiSelect-select.MuiInputBase-input": { minHeight: 37 } },
});

// The theme gives phone dialogs a 12px inset (with !important, inside a max-width media query);
// undo it, in the same media query so it wins, for the dialogs that go fullScreen there.
export const fullScreenPaperSx = (theme) => ({
  [theme.breakpoints.down("sm")]: {
    m: 0,
    width: "100%",
    maxWidth: "100% !important",
    maxHeight: "none",
  },
});
